/**
 * PaiementHeureSupModal - Self-service overtime payout request.
 *
 * Replaces the PAIEMENT_HEURE_SUP entry of the employee absence wizard: the
 * employee only picks how many of their available overtime hours to cash in
 * (0 excluded, balance as upper bound). Date and motivation are derived — the
 * backend derives nombreJours from dateDebut/dateFin via HeuresService.
 */

import { FormField } from "@components/forms/FormField";
import { Dialog } from "@components/ui/Dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTypeDemandes } from "@lib/hooks/collections/useTypeDemandes";
import { AlertCircle, Wallet } from "lucide-react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { TYPE_PAIEMENT_HEURE_SUP } from "../config/workflow.config";
import { useCreateAbsence } from "../hooks/useCreateAbsence";

/** Backend granularity: HeuresService floors any duration to the half-hour. */
const DUREE_STEP = 0.5;

/** Reference start time — the payout is a reference day, not a real work slot. */
const DUREE_DEBUT_HEURE = 8;

interface PaiementHeureSupModalProps {
  open: boolean;
  onClose: () => void;
  /** Available overtime balance, in hours (e.g. 5.5). */
  soldeHeures: number;
}

/**
 * Selectable amounts: every half-hour strictly above 0 and up to the balance.
 * Zero is unreachable by construction, as is any amount above the balance.
 */
function buildDureeOptions(soldeHeures: number): number[] {
  const count = Math.floor(soldeHeures / DUREE_STEP);
  return Array.from({ length: count }, (_, i) => (i + 1) * DUREE_STEP);
}

/** 5.5 -> "5 h 30", 5 -> "5 h" */
function formatHeures(heures: number): string {
  const minutes = Math.round(heures * 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m}`;
}

/**
 * Local-time "Y-m-d\TH:i:s" — the backend re-parses these as local times
 * (`new DateTime($data->dateDebut->format('Y-m-d\TH:i:s'))`), so a UTC ISO
 * string would shift the stored timestamps. The dateDebut/dateFin gap stays
 * exactly the requested duration, which is what nombreJours is derived from.
 */
function toLocalDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:00`
  );
}

export function PaiementHeureSupModal({ open, onClose, soldeHeures }: PaiementHeureSupModalProps) {
  const { data: typeDemandes } = useTypeDemandes();
  const { create, isLoading } = useCreateAbsence();

  // TypeDemande exposes id: null in standard JSON — resolve the id from the
  // collection to build the numeric IRI (/api/type_demandes/{id}).
  const typeDemandeId = typeDemandes?.find((t) => t.code === TYPE_PAIEMENT_HEURE_SUP)?.id;

  const dureeOptions = useMemo(() => buildDureeOptions(soldeHeures), [soldeHeures]);

  const schema = useMemo(
    () =>
      z.object({
        duree: z
          .string()
          .refine(
            (value) => value !== "" && Number(value) > 0 && Number(value) <= soldeHeures,
            `Choisissez un montant entre ${formatHeures(DUREE_STEP)} et ${formatHeures(soldeHeures)}`,
          ),
      }),
    [soldeHeures],
  );

  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { duree: "" },
  });

  const isSubmitting = form.formState.isSubmitting || isLoading;
  const isNotPayable = dureeOptions.length === 0;

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onSubmit = (values: FormValues) => {
    if (!typeDemandeId) return;

    const heures = Number(values.duree);
    const dateDebut = new Date();
    dateDebut.setHours(DUREE_DEBUT_HEURE, 0, 0, 0);
    const dateFin = new Date(dateDebut.getTime() + heures * 3_600_000);

    // No `personne` field: DemandeAbsenceCreationProcessor resolves the current
    // user's personne on the self-service branch.
    create(
      {
        typeDemande: `/api/type_demandes/${typeDemandeId}`,
        dateDebut: toLocalDateTime(dateDebut),
        dateFin: toLocalDateTime(dateFin),
        commentaire: `Demande de paiement de ${formatHeures(heures)} d'heures supplémentaires`,
        isUrgent: false,
      },
      { onSuccess: handleClose },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Paiement d'heures supplémentaires"
      subtitle={`Solde disponible : ${formatHeures(soldeHeures)}`}
      icon={<Wallet className="w-5 h-5" />}
      size="md"
      dismissable={!isSubmitting}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Annuler
          </button>
          <button
            type="submit"
            form="paiement-heures-sup-form"
            className="btn btn-primary"
            disabled={isSubmitting || isNotPayable}
          >
            {isSubmitting ? "Envoi..." : "Envoyer la demande"}
          </button>
        </div>
      }
    >
      <form
        id="paiement-heures-sup-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
      >
        <div className="alert alert-info text-sm">
          <AlertCircle className="w-5 h-5" />
          <span>
            Choisissez le nombre d'heures supplémentaires à payer. Le montant est débité de votre
            solde après validation de la demande par le RH.
          </span>
        </div>

        {isNotPayable ? (
          <div className="alert alert-warning text-sm">
            <AlertCircle className="w-5 h-5" />
            <span>Aucune heure supplémentaire à payer pour le moment.</span>
          </div>
        ) : (
          <FormField
            label="Heures à payer"
            icon={Wallet}
            required
            error={form.formState.errors.duree?.message}
            hint={`Par pas de ${formatHeures(DUREE_STEP)}, dans la limite de votre solde.`}
          >
            <select
              {...form.register("duree")}
              className={`select select-bordered w-full bg-base-200 border-base-300 ${
                form.formState.errors.duree ? "select-error" : ""
              }`}
            >
              <option value="">Sélectionnez...</option>
              {dureeOptions.map((heures) => (
                <option key={heures} value={heures}>
                  {formatHeures(heures)}
                </option>
              ))}
            </select>
          </FormField>
        )}
      </form>
    </Dialog>
  );
}
